/** Inventory tabs: stock per location, movement ledger and locations. */
import { useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Paginator } from "@primereact/ui/paginator";
import { Tag } from "@primereact/ui/tag";
import { Button } from "../../components/ui/Button.js";
import { Field } from "../../components/ui/Field.js";
import {
  CheckboxField,
  Modal,
  SearchBox,
} from "../../components/ui/controls.js";
import { cx } from "../../components/ui/cx.js";
import { apiGet, apiPatch, apiPost } from "../../lib/api.js";
import { usePermissions } from "../../lib/auth.js";
import {
  ErrorPanel,
  LoadingPanel,
  MutationError,
  formatMoney,
} from "../shared.js";
import {
  type Location,
  LocationFilter,
  type Operation,
  OperationButtons,
  PAGE_SIZE,
  type Page,
  StockOperationDialog,
  useLocations,
} from "./stock-operations.js";
import { MovementList } from "./movements.js";

export type Balance = {
  catalogItemId: string;
  locationId: string;
  onHand: string;
  averageUnitCost: string | null;
  item: {
    id: string;
    sku: string | null;
    name: string;
    reorderPoint: string | null;
  };
  location: { id: string; code: string; name: string };
};

export function StockTab() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [locationId, setLocationId] = useState("");
  const [operation, setOperation] = useState<Operation | null>(null);
  const lowOnly = params.get("stock") === "low";
  const balances = useQuery({
    queryKey: ["inventory-balances", page, q, locationId, lowOnly],
    queryFn: () =>
      apiGet<Page<Balance>>("/inventory/balances", {
        query: {
          page,
          pageSize: PAGE_SIZE,
          q: q || undefined,
          locationId: locationId || undefined,
          lowStock: lowOnly || undefined,
          sortField: "item",
          sortOrder: 1,
        },
      }),
    placeholderData: (previous) => previous,
  });
  const setLowOnly = (value: boolean) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set("stock", "low");
        else next.delete("stock");
        return next;
      },
      { replace: true },
    );
  return (
    <div className="space-y-4">
      <OperationButtons onStart={setOperation} />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_16rem_auto] sm:items-center">
        <SearchBox
          value={q}
          onChange={(value) => {
            setQ(value);
            setPage(1);
          }}
        />
        <LocationFilter
          value={locationId}
          onChange={(value) => {
            setLocationId(value);
            setPage(1);
          }}
        />
        <CheckboxField
          id="stock-low-only"
          checked={lowOnly}
          onChange={(value) => {
            setLowOnly(value);
            setPage(1);
          }}
          label={t("inventory.lowOnly")}
        />
      </div>
      {balances.isLoading ? (
        <LoadingPanel />
      ) : balances.error ? (
        <ErrorPanel error={balances.error} />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-app-border bg-app-surface">
          <table className="w-full min-w-[34rem] text-sm">
            <thead className="bg-app-background text-left text-xs uppercase tracking-wide text-app-muted">
              <tr>
                <th className="px-4 py-2 font-medium">{t("inventory.item")}</th>
                <th className="px-4 py-2 font-medium">
                  {t("inventory.location")}
                </th>
                <th className="px-4 py-2 text-right font-medium">
                  {t("inventory.onHand")}
                </th>
                <th className="px-4 py-2 text-right font-medium">
                  {t("inventory.value")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-border">
              {(balances.data?.data ?? []).map((balance) => {
                const low =
                  balance.item.reorderPoint !== null &&
                  Number(balance.onHand) <= Number(balance.item.reorderPoint);
                return (
                  <tr key={`${balance.catalogItemId}:${balance.locationId}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-medium">{balance.item.name}</span>
                      {balance.item.sku ? (
                        <span className="ml-2 text-xs text-app-muted">
                          {balance.item.sku}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-app-muted">
                      {balance.location.code} · {balance.location.name}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      <span className={cx(low && "font-semibold text-warning")}>
                        {Number(balance.onHand).toLocaleString()}
                      </span>
                      {low ? (
                        <Tag className="ml-2" severity="warn">
                          {t("inventory.lowStock")}
                        </Tag>
                      ) : null}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-app-muted">
                      {balance.averageUnitCost
                        ? formatMoney(
                            Number(balance.onHand) *
                              Number(balance.averageUnitCost),
                          )
                        : "—"}
                    </td>
                  </tr>
                );
              })}
              {!balances.data?.data.length ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-10 text-center text-app-muted"
                  >
                    {t("inventory.noStock")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {(balances.data?.total ?? 0) > PAGE_SIZE ? (
            <Paginator.Root
              className="border-t border-app-border py-2"
              page={page}
              total={balances.data?.total ?? 0}
              itemsPerPage={PAGE_SIZE}
              onPageChange={(event: { value: number }) => setPage(event.value)}
            />
          ) : null}
        </div>
      )}
      {operation ? (
        <StockOperationDialog
          operation={operation}
          onClose={() => setOperation(null)}
        />
      ) : null}
    </div>
  );
}

export function LocationsTab() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const locations = useLocations();
  const [draft, setDraft] = useState<
    (Omit<Location, "id"> & { id?: string }) | null
  >(null);
  const save = useMutation({
    mutationFn: () =>
      draft!.id
        ? apiPatch(`/inventory/locations/${draft!.id}`, {
            code: draft!.code,
            name: draft!.name,
            active: draft!.active,
            isDefault: draft!.isDefault,
          })
        : apiPost("/inventory/locations", {
            code: draft!.code,
            name: draft!.name,
            isDefault: draft!.isDefault,
          }),
    onSuccess: async () => {
      setDraft(null);
      await client.invalidateQueries({ queryKey: ["inventory-locations"] });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <div className="space-y-4">
      <Button
        type="button"
        size="small"
        icon="pi pi-plus"
        onClick={() =>
          setDraft({ code: "", name: "", active: true, isDefault: false })
        }
      >
        {t("inventory.addLocation")}
      </Button>
      {locations.isLoading ? (
        <LoadingPanel />
      ) : locations.error ? (
        <ErrorPanel error={locations.error} />
      ) : (
        <ul className="divide-y divide-app-border rounded-xl border border-app-border bg-app-surface">
          {(locations.data ?? []).map((location) => (
            <li
              key={location.id}
              className="flex items-center gap-3 px-4 py-3 text-sm"
            >
              <span className="w-20 font-mono text-xs text-app-muted">
                {location.code}
              </span>
              <span
                className={cx(
                  "flex-1 font-medium",
                  !location.active && "text-app-muted line-through",
                )}
              >
                {location.name}
              </span>
              {location.isDefault ? (
                <Tag severity="info">{t("inventory.default")}</Tag>
              ) : null}
              <Button
                type="button"
                size="small"
                variant="text"
                icon="pi pi-pencil"
                aria-label={t("common.edit")}
                onClick={() => setDraft({ ...location })}
              />
            </li>
          ))}
          {!locations.data?.length ? (
            <li className="px-4 py-8 text-center text-sm text-app-muted">
              {t("inventory.noLocations")}
            </li>
          ) : null}
        </ul>
      )}
      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={
          draft?.id ? t("inventory.editLocation") : t("inventory.addLocation")
        }
      >
        {draft ? (
          <form className="space-y-4" onSubmit={submit}>
            <MutationError error={save.error} />
            <div className="grid grid-cols-[8rem_1fr] gap-3">
              <Field label={t("inventory.code")} required>
                <Input
                  fluid
                  required
                  maxLength={20}
                  value={draft.code}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setDraft({ ...draft, code: event.target.value })
                  }
                />
              </Field>
              <Field label={t("common.name")} required>
                <Input
                  fluid
                  required
                  value={draft.name}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                />
              </Field>
            </div>
            <CheckboxField
              id="location-default"
              checked={draft.isDefault}
              onChange={(isDefault) => setDraft({ ...draft, isDefault })}
              label={t("inventory.default")}
            />
            {draft.id ? (
              <CheckboxField
                id="location-active"
                checked={draft.active}
                onChange={(active) => setDraft({ ...draft, active })}
                label={t("common.active")}
              />
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outlined"
                severity="secondary"
                onClick={() => setDraft(null)}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={save.isPending}>
                {t("common.save")}
              </Button>
            </div>
          </form>
        ) : null}
      </Modal>
    </div>
  );
}

export function InventoryTabs({
  tab,
}: {
  tab: "stock" | "movements" | "locations";
}) {
  const permissions = usePermissions();
  if (!permissions.has("inventory.view")) return null;
  if (tab === "stock") return <StockTab />;
  if (tab === "movements") return <MovementList />;
  if (permissions.has("inventory.manage_locations")) return <LocationsTab />;
  return null;
}
